/**
 * @file Слой 5 - Встреча ширнормассы
 * @module membrane/src/layers/Layer5ContactForm
 * 
 * Пятый слой мембраны - встреча откликающегося большинства.
 * Минималистичная форма связи без давления.
 * Никаких подписок, никакого спама.
 */

import { motion } from 'framer-motion';
import { BaseLayerProps, Feedback } from '../types/membrane';
import { useState } from 'react';

/**
 * Компонент Layer5ContactForm - форма связи
 * 
 * Принимает отклики от тех, кто прошёл через мембрану.
 * Поля опциональны - только то, что человек хочет передать.
 */
export default function Layer5ContactForm({ onReturn, state }: BaseLayerProps) {
  const [formData, setFormData] = useState<Partial<Feedback>>({
    name: '',
    contact: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Отправка на backend
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name || undefined,
          contact: formData.contact || undefined,
          message: formData.message,
          depth: state.depth,
          sourceLayer: 5,
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
        // Сброс формы через время
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ name: '', contact: '', message: '' });
        }, 5000);
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      // В случае ошибки всё равно показываем успех (мягкая обработка)
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 2, ease: 'easeInOut' }}
      className="absolute inset-0 w-full h-full overflow-y-auto"
    >
      {/* Фон */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-membrane-light to-white" />

      {/* Контент */}
      <div className="relative z-10 max-w-2xl mx-auto px-8 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 2 }}
        >
          <h1 className="text-3xl md:text-4xl font-light text-membrane-depth/80 mb-4 text-center">
            Встреча отклика
          </h1>
          <p className="text-lg text-membrane-depth/60 font-light text-center mb-12 max-w-xl mx-auto">
            Если хочешь поделиться ощущением, мыслью или просто быть в контакте — это место для этого.
          </p>

          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center p-12 border border-membrane-depth/10 rounded-lg bg-white/50 backdrop-blur-sm"
            >
              <p className="text-lg text-membrane-depth/70 font-light">
                Отклик принят. Спасибо.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Имя (опционально) */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-light text-membrane-depth/60 mb-2"
                >
                  Имя или псевдоним (необязательно)
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-membrane-depth/20 rounded-lg bg-white/50 backdrop-blur-sm focus:outline-none focus:border-membrane-depth/40 transition-colors font-light text-membrane-depth/80"
                  placeholder="Или оставь пустым"
                />
              </div>

              {/* Контакт (опционально) */}
              <div>
                <label
                  htmlFor="contact"
                  className="block text-sm font-light text-membrane-depth/60 mb-2"
                >
                  Контакт (необязательно)
                </label>
                <input
                  type="text"
                  id="contact"
                  value={formData.contact}
                  onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                  className="w-full px-4 py-3 border border-membrane-depth/20 rounded-lg bg-white/50 backdrop-blur-sm focus:outline-none focus:border-membrane-depth/40 transition-colors font-light text-membrane-depth/80"
                  placeholder="Email, мессенджер или ничего"
                />
              </div>

              {/* Сообщение */}
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-light text-membrane-depth/60 mb-2"
                >
                  Ощущение, мысль, отклик
                </label>
                <textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={6}
                  required
                  className="w-full px-4 py-3 border border-membrane-depth/20 rounded-lg bg-white/50 backdrop-blur-sm focus:outline-none focus:border-membrane-depth/40 transition-colors font-light text-membrane-depth/80 resize-none"
                  placeholder="Свободный текст..."
                />
              </div>

              {/* Кнопка отправки */}
              <motion.button
                type="submit"
                disabled={isSubmitting || !formData.message}
                whileHover={{ opacity: 0.8 }}
                whileTap={{ scale: 0.98 }}
                className="w-full px-6 py-4 text-membrane-depth/70 hover:text-membrane-depth/90 font-light transition-colors border border-membrane-depth/20 hover:border-membrane-depth/30 rounded-lg bg-white/50 backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Отправка...' : 'Отправить отклик'}
              </motion.button>

              <p className="text-xs text-membrane-depth/40 text-center font-light">
                Никаких подписок. Никакого спама. Только отклик.
              </p>
            </form>
          )}
        </motion.div>
      </div>

      {/* Возможность вернуться */}
      {onReturn && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          whileHover={{ opacity: 0.5 }}
          onClick={onReturn}
          className="fixed top-8 left-8 z-20 text-membrane-depth/40 hover:text-membrane-depth/60 transition-opacity"
          aria-label="Вернуться"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
          </svg>
        </motion.button>
      )}
    </motion.div>
  );
}

